import moment from 'moment';
import { getUserPool } from '../../lib/pools/pools';
import { checkAccessLevel } from '../../lib/authenticate';

export interface DayContext {
  dayDate: moment.Moment;
  dayIndex?: number;
  isDarkMode: boolean;
  availabilityMode: boolean;
  availabilityColor: string;
  holidayName?: string | null;
  submitNextScheduleDate: boolean;
  employeeCuttoffDate: string;
  isCurrentMonth?: boolean;
  isToday?: boolean;
}

export interface ShiftRenderContext {
  shift: any;
  idx: number;
  dayDate: moment.Moment;
  assignments: any[];
  positions: any[];
  filteredShifts: any[];
  accessLevel: string | null;
  employeeCuttoffDate: string;
}

export interface ShiftCheckResult {
  assigned: boolean;
  hasShiftPosition: boolean;
  foundCancel: boolean;
  customAssignments: any;
  assignedStaffCount: number;
  color: string;
  shouldRender: boolean;
}

// Check if a shift is assigned to the current user
export const isShiftAssignedToUser = (
  shift: any,
  customAssignments: any,
  username: string
): boolean => {
  if (customAssignments) {
    return customAssignments.assignees?.includes(username) ?? false;
  }
  return shift?.assignedStaff?.includes(username) ?? false;
};

// Check if user has the position for this shift
export const userHasShiftPosition = (
  shift: any,
  positions: any[],
  username: string
): boolean => {
  return positions.some(
    (position) =>
      position.position === shift.position &&
      position.employees?.includes(username)
  );
};

// Check if shift is cancelled
export const isShiftCancelled = (
  shift: any,
  allFilteredShifts: any[],
  dayDate: moment.Moment
): boolean => {
  if (shift?.cancelShiftId) return true;

  return allFilteredShifts.some(
    (s) =>
      shift?.shiftId === s?.shiftReferenceId &&
      s?.shiftDate === dayDate.format('YYYY-MM-DD')
  );
};

// Get the color for a shift based on staffing level
export const getShiftColor = (
  shift: any,
  assignedStaffCount: number,
  accessLevel: string | null
): string => {
  if (!checkAccessLevel(accessLevel)) {
    // Staff member view
    return assignedStaffCount > 0 ? 'primary' : 'info';
  }

  // Owner/manager view
  const needed = parseInt(shift?.staffNeeded || '0');
  if (needed === assignedStaffCount) return 'primary';
  if (assignedStaffCount === 0) return 'error';
  return 'info';
};

// Validate if shift should be rendered on a specific day
export const shouldRenderShift = (
  shift: any,
  dayDate: moment.Moment,
  hasPosition: boolean,
  isAssigned: boolean,
  accessLevel: string | null,
  employeeCuttoffDate: string
): boolean => {
  // Check if shift is on this day
  const isOnThisDay =
    (!shift?.isRecurring &&
      shift?.shiftDate === dayDate.format('YYYY-MM-DD')) ||
    (shift?.isRecurring &&
      shift?.daysOfWeek?.includes(dayDate.format('dddd').toLowerCase()));

  if (!isOnThisDay) return false;

  // Check access and permissions
  const hasAccess =
    checkAccessLevel(accessLevel) || hasPosition || isAssigned;
  if (!hasAccess) return false;

  // Check cutoff date
  const isBeforeCutoff = employeeCuttoffDate >= dayDate.format('YYYY-MM-DD');
  if (!checkAccessLevel(accessLevel) && !isBeforeCutoff) return false;

  return true;
};

// Get custom assignments for a shift on a specific day
export const getCustomAssignmentsForShift = (
  shiftId: string,
  dayDate: moment.Moment,
  assignments: any[]
): any | null => {
  return (
    assignments.find(
      (assignment) =>
        assignment.shiftId === shiftId &&
        assignment.date === dayDate.format('YYYY-MM-DD')
    ) || null
  );
};

// Complete shift check for rendering
export const checkShiftForRendering = (
  context: ShiftRenderContext
): ShiftCheckResult => {
  const username = getUserPool()?.getCurrentUser()?.getUsername() || '';

  const customAssignments = getCustomAssignmentsForShift(
    context.shift.shiftId,
    context.dayDate,
    context.assignments
  );

  const assigned = isShiftAssignedToUser(
    context.shift,
    customAssignments,
    username
  );

  const hasShiftPosition = userHasShiftPosition(
    context.shift,
    context.positions,
    username
  );

  const foundCancel = isShiftCancelled(
    context.shift,
    context.filteredShifts,
    context.dayDate
  );

  const assignedStaffCount = customAssignments
    ? customAssignments.assignees?.length
    : context.shift.assignedStaff?.length;

  const color = getShiftColor(
    context.shift,
    assignedStaffCount,
    context.accessLevel
  );

  const shouldRender = shouldRenderShift(
    context.shift,
    context.dayDate,
    hasShiftPosition,
    assigned,
    context.accessLevel,
    context.employeeCuttoffDate
  );

  return {
    assigned,
    hasShiftPosition,
    foundCancel,
    customAssignments,
    assignedStaffCount,
    color,
    shouldRender: shouldRender && !foundCancel && !context.shift?.cancelShiftId,
  };
};
